/**
 * The client book, counted.
 *
 * Where the sales report asks "what came in and what became of it", this
 * asks the four questions people actually ask of a book of 1,332
 * accounts:
 *
 *   which plan      how many are on FREE, STARTER, GROWTH, SUITE — and
 *                   what each of those tiers is worth
 *   which code      who arrived on a promo code, and which codes those
 *                   were, because a code used ninety times is a channel
 *   who has paid    money actually recorded, not a plan chosen
 *   who is using it two different questions wearing one word — see below
 *
 * ## "Active" is two questions, so it is answered twice
 *
 * The status column says Active for nearly every account on the book, so
 * counting it produces a number that cannot move. Signing in is the one
 * that tells anybody whether the product is being used. Both get asked,
 * so both are here and neither borrows the other's name:
 *
 *   on the book     status is one of `S.liveStatuses` — switched on
 *   signed in       `lastActivity` inside a window of 7, 14, 30 or 60 days
 *
 * The windows count back from **today**, never from the date filter. "Who
 * has been in this week" does not change because somebody narrowed the
 * page to accounts that joined in March.
 *
 * ## The window picks by sign-up date
 *
 * Like every other date filter over the client book, the range measures
 * `regOn` — "which accounts came in during this period". A row with no
 * registration date cannot be shown to fall inside a window, so it drops
 * out of a narrowed page; All time keeps everybody.
 *
 * ## Nothing is recounted anywhere else
 *
 * Every figure here comes from `deriveClient` and `pricing`, the same two
 * modules the tiles, the filters and the sidebar badges read. The screen
 * hands what it counted to the server for the exports, so the workbook,
 * the PDF and the page cannot disagree about one word.
 */
import { parseDate, startOfDay, addDays, daysAgo } from '../lib/dates.js';
import { inDateRange, SEEN_WINDOWS } from './derive.js';
import { amountOf, mrrOf, priceOf, planAmount, statedAmount } from './pricing.js';

/* ------------------------------------------------------------- the window */

const pad = n => String(n).padStart(2, '0');

/** A Date as the `yyyy-mm-dd` a date input speaks. */
export const dayOf = d =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/**
 * The windows on offer.
 *
 * All time leads, and is the default, because the first figure anybody
 * wants off this page is how many clients there are — not how many
 * arrived lately. The spans after it are the ones asked next.
 */
export const RANGES = [
  ['all', 'All time'],
  ['today', 'Today'],
  ['7d', 'Last 7 days'],
  /* The same four the app's own 'last seen' buckets use, so picking a
     window here and reading the Active card under it are one question
     and not two - see SEEN_WINDOWS. */
  ['14d', 'Last 14 days'],
  ['30d', 'Last 30 days'],
  ['60d', 'Last 60 days'],
  ['90d', 'Last 90 days'],
  ['year', 'This year'],
  ['custom', 'Custom']
];

/**
 * One window, as a pair of days.
 *
 * Both ends are **included**, unlike the demo and sales reports: those
 * count timestamped events, where an exclusive end is the only way two
 * windows can meet without overlapping at midnight. A registration date
 * is a plain day, and "1 Sep to 30 Sep" is what somebody typing two dates
 * into this page means.
 */
export function rangeOf(key, custom = {}) {
  const now = new Date();
  const today = dayOf(now);

  const span = (from, to, label) => ({
    key,
    from,
    to,
    label,
    days: Math.round((startOfDay(parseDate(to)) - startOfDay(parseDate(from))) / 864e5) + 1
  });

  if (key === 'today') return span(today, today, 'Today');
  if (key === '7d') return span(dayOf(addDays(now, -6)), today, 'Last 7 days');
  if (key === '14d') return span(dayOf(addDays(now, -13)), today, 'Last 14 days');
  if (key === '30d') return span(dayOf(addDays(now, -29)), today, 'Last 30 days');
  if (key === '60d') return span(dayOf(addDays(now, -59)), today, 'Last 60 days');
  if (key === '90d') return span(dayOf(addDays(now, -89)), today, 'Last 90 days');
  if (key === 'year') return span(`${now.getFullYear()}-01-01`, today, String(now.getFullYear()));

  if (key === 'custom') {
    /* Either end may be missing or the pair the wrong way round, because
       it is two date boxes somebody is still typing into. */
    const a = custom.from || custom.to || today;
    const b = custom.to || custom.from || today;
    const lo = a <= b ? a : b;
    const hi = a <= b ? b : a;
    return span(lo, hi, lo === hi ? lo : `${lo} to ${hi}`);
  }

  /* All time carries no dates at all, so `inDateRange` lets everything
     through — including the accounts with no registration date on them. */
  return { key: 'all', from: '', to: '', label: 'All time', days: null };
}

/* -------------------------------------------------------------- the fields */

/** The plan on a row, uppercased, never blank. */
export const planOf = r => String(r.plan || '').trim().toUpperCase() || 'NO PLAN';

/** The promo code on a row, uppercased — blank means they came in on none. */
export const promoOf = r => String(r.promo || '').trim().toUpperCase();

/** Whose name is on it, never blank. */
export const ownerOf = r => String(r.handler || '').trim() || 'Unassigned';

/** What the payment column says, never blank. */
export const payOf = r => String(r.pay || '').trim().toUpperCase() || 'NOT SET';

/** The cycle the record actually carries, or '' where it carries none. */
export const cycleStated = r => {
  const c = String(r.cycle || '').trim();
  return !c || c === '-' ? '' : c;
};

/**
 * How they are billed.
 *
 * XYRA bills monthly or yearly, so "No cycle" was never an arrangement —
 * it was a blank field, and putting it on the billing table as a third row
 * invented a way of paying that the business does not offer. One account
 * on the book is in that state: STARTER, AED 49, with `cycle` null from
 * the clients app.
 *
 * So a blank is read off the money instead. The workspace already knows
 * what a plan costs a month and what it costs a year, and 49 against a
 * STARTER is a month where 470 is a year — the account tells us which it
 * is by what it hands over. Nothing is guessed where a cycle is recorded;
 * this only fills a hole.
 *
 * Monthly is the fallback when the amount settles nothing, which is the
 * same assumption `cycleMonths` has always made — so the label and the MRR
 * cannot disagree about one account.
 */
export function cycleOf(S, r) {
  const stated = cycleStated(r);
  if (stated) return stated;

  const paid = statedAmount(r);
  if (paid === null) return 'Monthly';

  const year = planAmount(S, { ...r, cycle: 'Annual' });
  const month = planAmount(S, { ...r, cycle: 'Monthly' });
  if (year > 0 && Math.abs(paid - year) < Math.abs(paid - month)) return 'Annual';
  return 'Monthly';
}

/** A whole number percentage, or null where there is nothing to divide by. */
export const share = (n, of) => (of ? Math.round((n / of) * 100) : null);

/* -------------------------------------------------------------- the counts */

/**
 * Has somebody opened XYRA inside a window of `days` calendar days
 * ending today?
 *
 * `days` is a length, not a distance back: Today is 1 and reaches no
 * further than this morning, Last 7 days is 7 and reaches back six. The
 * test used to be `dAct <= days`, which is one day too many at every
 * window - Today counted yesterday as well and read 160 against the 110
 * the clients page had just shown, and Last 7 days quietly spanned eight.
 *
 * Every window on both screens comes through here, so the two cannot
 * disagree about what a day is again.
 */
export const seenWithin = (d, days) =>
  d.dAct !== null && d.dAct <= Math.max(0, (days || 1) - 1);

/**
 * Paid, but with nothing to show for it.
 *
 * The payment column says YES and the record carries no amount — so the
 * account is counted as paying and is worth nothing, which reads as a
 * contradiction on any screen that prints the money. It is neither: it is
 * a gap in the clients app that somebody has to go and fill, and the only
 * way anybody finds out is if a report says how many there are.
 */
export const paidWithNoAmount = (S, x) => !!x.d.paid && !amountOf(S, x.r);

const blank = extra => ({
  total: 0, paid: 0, blankAmount: 0, live: 0, promo: 0, seen: 0, value: 0, mrr: 0, rows: [], ...extra
});

/**
 * Add one account to a bucket.
 *
 * `value` is what the account hands over each cycle and `mrr` the same
 * money normalised to a month, so a bucket holding one annual 950 and one
 * monthly 49 can be compared with a bucket holding twelve monthlies.
 * Both count only accounts with a payment recorded: a plan's list price
 * against a free account is not revenue.
 */
function tally(bucket, S, x, primary) {
  bucket.total += 1;
  if (x.d.paid) {
    bucket.paid += 1;
    bucket.value += amountOf(S, x.r);
    bucket.mrr += mrrOf(S, x.r, x.d);
    if (paidWithNoAmount(S, x)) bucket.blankAmount += 1;
  }
  if (x.d.live) bucket.live += 1;
  if (promoOf(x.r)) bucket.promo += 1;
  if (seenWithin(x.d, primary)) bucket.seen += 1;
  bucket.rows.push(x);
}

/**
 * Plans in the order the business sells them. One card each, and no more.
 *
 * `S.plans` is the ladder somebody set up in Settings — the plans that
 * are actually sold. A retired tier like PROFESSIONAL is not on it, and
 * a card for it read as a fifth thing on offer, which is the one thing
 * this section must not say. So the cards are the ladder, plus "no
 * plan" last because it is an absence rather than a tier.
 *
 * The accounts still on a retired plan are **not** dropped — they are
 * counted by `offLadder` and said in a line under the cards. Losing
 * seventeen accounts out of a plan breakdown without saying so would be
 * worse than the extra card was.
 */
function planOrder(S, found) {
  const ladder = (S.plans || []).map(p => String(p).toUpperCase());
  return ladder.filter(k => found.has(k));
}

/* Accounts on a plan nobody sells any more. Whole buckets, the same
   shape as byPlan, so the exports can put them back on the end of the
   table without rebuilding anything. */
function offLadderPlans(S, found) {
  const ladder = (S.plans || []).map(p => String(p).toUpperCase());
  return [...found.entries()]
    .filter(([k]) => !ladder.includes(k))
    .map(([, v]) => v)
    .sort((a, b) => b.total - a.total || String(a.key).localeCompare(String(b.key)));
}

/**
 * The whole report, from the rows the browser already has.
 *
 * `derive` is the store's memoised `deriveClient`, so a book of 1,332
 * accounts is walked once and nothing is parsed twice.
 */
export function summarise(S, rows, derive, f = {}) {
  const { from = '', to = '', plan = '', owner = '', primary = SEEN_WINDOWS[0] } = f;

  let cohort = (rows || []).map(r => ({ r, d: derive(r) }));
  if (from || to) cohort = cohort.filter(x => inDateRange(x.r, { from, to }));
  if (plan) cohort = cohort.filter(x => planOf(x.r) === plan);
  if (owner) cohort = cohort.filter(x => ownerOf(x.r) === owner);

  /*
   * The same book, without the dates.
   *
   * The date filter on this page means "signed up in this window", and
   * every figure that counts sign-ups wants that. Active and not active
   * does not: "how many of our clients opened XYRA today" is a question
   * about all of them, and answering it inside the day's own sign-ups
   * gave 25 active and 0 not active, because all 25 accounts created
   * today had of course opened it today. The window still comes from the
   * filter — it is the *last seen* window — but the people it is asked
   * of are the whole book.
   *
   * Plan and owner do still narrow it. Those say which clients, not when.
   */
  let book = (rows || []).map(r => ({ r, d: derive(r) }));
  if (plan) book = book.filter(x => planOf(x.r) === plan);
  if (owner) book = book.filter(x => ownerOf(x.r) === owner);

  /* Newest first throughout. Every list this report opens is read for
     "who is on it now", and the most recent sign-ups are the rows
     somebody is most likely to be looking for. */
  cohort.sort((a, b) => (parseDate(b.r.regOn) || 0) - (parseDate(a.r.regOn) || 0));

  const total = blank();
  const plans = new Map();
  const codes = new Map();
  const pays = new Map();
  const cycles = new Map();

  cohort.forEach(x => {
    const p = planOf(x.r);
    const code = promoOf(x.r);

    if (!plans.has(p)) plans.set(p, { key: p, price: priceOf(S, p), ...blank() });
    tally(total, S, x, primary);
    tally(plans.get(p), S, x, primary);

    if (code) {
      if (!codes.has(code)) codes.set(code, { key: code, ...blank({ plans: new Set() }) });
      const hit = codes.get(code);
      tally(hit, S, x, primary);
      hit.plans.add(p);
    }

    const pk = payOf(x.r);
    if (!pays.has(pk)) pays.set(pk, { key: pk, ...blank() });
    tally(pays.get(pk), S, x, primary);

    /* Cycles are read to answer "what are we billing, and how often", so
       only the accounts that actually pay are on that table. A free
       account with a blank cycle is not a billing arrangement. */
    if (x.d.paid) {
      const ck = cycleOf(S, x.r);
      if (!cycles.has(ck)) cycles.set(ck, { key: ck, ...blank(), inferred: 0 });
      tally(cycles.get(ck), S, x, primary);
      /* Filed here off what they pay rather than off a cycle on the record.
         Counted so the row can say so — a figure nobody can trace back to a
         field is a figure somebody eventually calls wrong. */
      if (!cycleStated(x.r)) cycles.get(ck).inferred += 1;
    }
  });

  const byPlan = planOrder(S, plans).map(k => plans.get(k));
  /* Counted, named, and kept out of the cards — see planOrder. */
  const offLadder = offLadderPlans(S, plans);

  /*
   * Codes, most-used first — and the ones that brought money ahead of
   * the ones that only brought sign-ups where the count ties. Four
   * hundred of the twelve hundred accounts carry a code and most of
   * those codes were used once, so the order is the whole point: the
   * handful at the top are partners somebody can ring.
   */
  const byPromo = [...codes.values()]
    /*
     * `newest` is when the code last brought somebody in.
     *
     * A count alone cannot tell a channel that is still running from one
     * that stopped in March, and for a partner code that is the whole
     * question. The cohort is sorted newest-first above, so the first row
     * in each bucket is the latest arrival — no second sort.
     */
    .map(c => ({ ...c, plans: [...c.plans].sort(), newest: c.rows[0] ? c.rows[0].d.reg : null }))
    .sort((a, b) => b.total - a.total || b.paid - a.paid || a.key.localeCompare(b.key));

  const promoRows = cohort.filter(x => promoOf(x.r));
  const plainRows = cohort.filter(x => !promoOf(x.r));

  /*
   * Counted here, left out of the tile strip on the list beside this.
   *
   * `S.excluded` is Internal and Test by default, and every figure the
   * client *list* shows drops them. This report counts the whole window,
   * because "1,332 clients" is the number the sidebar says and the number
   * people quote. Where the two would differ the page says so rather than
   * letting one screen carry two totals without explaining itself.
   */
  const excluded = cohort.filter(x => x.d.excluded).length;

  const paidRows = cohort.filter(x => x.d.paid);
  const unpaidRows = cohort.filter(x => !x.d.paid);

  const liveRows = cohort.filter(x => x.d.live);
  const dormantRows = cohort.filter(x => !x.d.live);
  const neverRows = cohort.filter(x => x.d.dAct === null);
  /* Asked of the book, like the two cards it sits beside. */
  const neverOnBook = book.filter(x => x.d.dAct === null);

  /*
   * One row per window, and the two halves of each are the whole cohort
   * between them: signed in inside the window, and everybody else. The
   * ones the app has never seen are inside "not active" at every window —
   * they are counted again on their own because "never opened it" is a
   * different conversation from "went quiet in May".
   */
  /*
   * The four standard windows, and the one the page is asking about.
   *
   * Picking Today and being told how many came in "this week" is the page
   * answering a question nobody asked — so the window the period names is
   * counted too, and the tile at the top reads from it. The four stay
   * because they are the ladder people size a problem against, and a
   * report that showed only the selected span could not do that.
   */
  const spans = [...new Set([primary, ...SEEN_WINDOWS])].sort((a, b) => a - b);

  const windows = spans.map(days => {
    /* `book`, not `cohort` — see the note where book is built. */
    const active = book.filter(x => seenWithin(x.d, days));
    const idle = book.filter(x => !seenWithin(x.d, days));
    return {
      days,
      primary: days === primary,
      active: active.length,
      idle: idle.length,
      activeRows: active,
      idleRows: idle,
      pct: share(active.length, book.length)
    };
  });

  return {
    cohort,
    total,
    excluded,
    primary,
    byPlan,
    offLadder,
    byPromo,
    byPay: [...pays.values()].sort((a, b) => b.total - a.total),
    byCycle: [...cycles.values()].sort((a, b) => b.value - a.value || b.total - a.total),
    promo: {
      on: promoRows.length,
      off: plainRows.length,
      codes: byPromo.length,
      paid: promoRows.filter(x => x.d.paid).length,
      value: promoRows.reduce((t, x) => t + (x.d.paid ? amountOf(S, x.r) : 0), 0),
      rows: promoRows,
      plainRows
    },
    payment: {
      done: paidRows.length,
      notDone: unpaidRows.length,
      value: total.value,
      mrr: total.mrr,
      /* Counted as paying, worth nothing — see paidWithNoAmount. Kept
         beside the money so the two are read together. */
      blankAmount: total.blankAmount,
      blankRows: paidRows.filter(x => paidWithNoAmount(S, x)),
      rows: paidRows,
      unpaidRows
    },
    activity: {
      live: liveRows.length,
      dormant: dormantRows.length,
      never: neverRows.length,
      /* The same question asked of the book, for the two cards. */
      neverOnBook: neverOnBook.length,
      neverOnBookRows: neverOnBook,
      liveRows,
      dormantRows,
      neverRows,
      windows
    }
  };
}

/** Every plan on the book, for the picker — not only the ones in range. */
export function plansOf(S, rows) {
  const found = new Set((rows || []).map(planOf));
  const ladder = (S.plans || []).map(p => String(p).toUpperCase());
  const rest = [...found].filter(k => !ladder.includes(k)).sort();
  return [...ladder.filter(k => found.has(k)), ...rest];
}

/** Every owner on the book, same reason. Unassigned sinks to the bottom. */
export function ownersOf(rows) {
  return [...new Set((rows || []).map(ownerOf))].sort((a, b) => {
    if (a === 'Unassigned') return 1;
    if (b === 'Unassigned') return -1;
    return a.localeCompare(b);
  });
}

/* ---------------------------------------------------------------- one row */

/** How a window is named, so the tile, the card and the sheet agree. */
export const windowLabel = days =>
  (days === 1 ? 'today' : days === 7 ? 'this week' : `in ${days} days`);

/** The same window on a row of its own. */
export const windowRow = days => (days === 1 ? 'Today' : `Last ${days} days`);

/**
 * How to say what the app has seen of somebody, in three words.
 *
 * `null` days means the app has never seen them, which is not the same as
 * a large number and should not read as one.
 */
export function seenText(d) {
  if (d.dAct === null) return 'Never signed in';
  if (d.dAct === 0) return 'In today';
  return `${d.dAct}d ago`;
}

/**
 * A client, flattened for a table or a file.
 *
 * The same shape feeds the drill-down on screen and the rows in both
 * exports, so what somebody reads on the page is what lands in the file.
 * Dates are formatted here rather than at each end, because the raw value
 * is whatever the clients app sent and a spreadsheet shows an ISO string
 * as "2026-09-29T04:06:0…" in whatever width the column happens to be.
 *
 * Money and day counts stay **numbers**. A workbook column of numbers
 * sorts, totals and right-aligns; the same column as text does none of
 * those, and that is the difference between a file somebody can work in
 * and a picture of one.
 */
export function lineOf(S, x, fmtDate) {
  const { r, d } = x;
  const date = v => {
    const parsed = parseDate(v);
    return parsed ? fmtDate(parsed) : '';
  };

  return {
    id: r.id,
    name: r.name || 'Untitled',
    email: r.email || '',
    phone: r.phone || '',
    contact: r.contact || '',
    plan: planOf(r),
    type: d.type || '',
    status: r.status || '',
    cycle: cycleOf(S, r),
    promo: promoOf(r),
    owner: ownerOf(r),
    source: r.source || '',
    pay: payOf(r),
    paid: d.paid ? 'Yes' : 'No',
    /* What they hand over each cycle, and only where money has actually
       been recorded — see amountOf on why a blank is not a list price. */
    amount: d.paid ? Math.round(amountOf(S, r)) : 0,
    monthly: d.paid ? Math.round(mrrOf(S, r, d)) : 0,
    onBook: d.live ? 'Yes' : 'No',
    joined: date(r.regOn),
    /* Sortable alongside the formatted day, so the workbook can order by
       sign-up without anybody parsing "29 Sep 2026" back again. */
    joinedOn: r.regOn || '',
    joinedDays: r.regOn && parseDate(r.regOn) ? daysAgo(parseDate(r.regOn)) : '',
    lastSeen: date(r.lastActivity),
    seenDays: d.dAct === null ? '' : d.dAct,
    seenText: seenText(d),
    trialEnd: d.noTrial ? 'No trial' : date(r.trialEnd),
    expiry: date(r.expiry),
    nextDue: date(r.nextDue),
    lastPaid: date(r.lastPaid),
    flag: d.flags.length ? d.flags[0].txt : ''
  };
}

/* --------------------------------------------------------- the drill-downs */

/**
 * The rows behind one figure.
 *
 * Every number on the page opens on the rows it was counted from rather
 * than on a rule for finding them again: a sheet that recomputed "not
 * active in 30 days" from the whole book would open on a thousand rows
 * under a heading that said four hundred.
 *
 * The cut arrives as a string, because it has to survive being held in
 * one piece of state and read back:
 *
 *   all                  everybody in the window
 *   paid | unpaid        money recorded, or not
 *   onbook | dormant     the status column
 *   promo | nopromo      arrived on a code, or not
 *   never                the app has never seen them
 *   plan:SUITE           one plan
 *   plan:SUITE:paid      one plan, opened on its paying half
 *   code:PARTNER10       one promo code
 *   seen:30 | idle:30    one activity window, either half
 *   pay:PENDING          one value of the payment column
 *   cycle:Annual         one billing cycle, paying accounts only
 *
 * A third part on a plan names a **tab**, not a narrower scope. The sheet's
 * tabs are already the cuts a plan card's boxes stand for, so a box opens
 * the plan with its own tab chosen — and whoever came for the paying half
 * can see the rest without closing anything. Narrowing the scope instead
 * left four of the five tabs reading zero.
 */
export function cutOf(report, cut) {
  const [a, b, c] = String(cut || 'all').split(':');
  const r = report;

  /*
   * `tabs: false` means the sheet opens as one list with no cuts across it.
   *
   * A figure opens on what it named and nothing else. Somebody who presses
   * "Payment done" wants the accounts that paid; offering them "Not paid"
   * and "In the last 7 days" over the top adds four buttons to answer a
   * question they did not ask, and most of them read zero — which makes
   * the sheet look emptier than the card that opened it.
   *
   * Two keep their tabs, because there the cuts are the point: the whole
   * window, which is every account and no cut at all, and a plan, whose
   * card carries four boxes that each open it on one of them.
   */
  const plain = (rows, title, sub, tab, tabs = false) => ({
    rows, title, sub, tab: tab || 'all', tabs
  });

  if (a === 'plan') {
    /* A retired tier has no card, but it still has rows and the line
       under the cards names it - so the cut has to find it. */
    const hit = r.byPlan.find(x => x.key === b)
      || (r.offLadder || []).find(x => x.key === b);
    if (!hit) return null;
    return plain(hit.rows, `${hit.key} plan`, 'Every account on this plan', c, true);
  }

  if (a === 'code') {
    const hit = r.byPromo.find(x => x.key === b);
    if (!hit) return null;
    return plain(hit.rows, hit.key, 'Everybody who arrived on this promo code');
  }

  if (a === 'seen' || a === 'idle') {
    const hit = r.activity.windows.find(w => String(w.days) === String(b));
    if (!hit) return null;
    return a === 'seen'
      ? plain(hit.activeRows, `Signed in within ${hit.days} days`,
        'They have opened XYRA inside this window')
      : plain(hit.idleRows, `Not in for ${hit.days} days`,
        'Nothing since — including the accounts the app has never seen');
  }

  if (a === 'pay') {
    const hit = r.byPay.find(x => x.key === b);
    if (!hit) return null;
    return plain(hit.rows, `Payment: ${hit.key}`, 'What the payment column says on the record');
  }

  if (a === 'cycle') {
    const hit = r.byCycle.find(x => x.key === b);
    if (!hit) return null;
    return plain(hit.rows, `${hit.key} billing`, 'Paying accounts billed on this cycle');
  }

  if (a === 'paid') return plain(r.payment.rows, 'Payment done', 'The payment column says they have paid');
  if (a === 'noamount') {
    return plain(r.payment.blankRows, 'Paid, with no amount recorded',
      'The payment column says yes and the record carries no figure — nothing to bill against');
  }
  if (a === 'unpaid') return plain(r.payment.unpaidRows, 'Nothing recorded', 'No payment against them yet');
  if (a === 'onbook') return plain(r.activity.liveRows, 'On the book', 'Status says the account is switched on');
  if (a === 'dormant') return plain(r.activity.dormantRows, 'Off the book', 'Inactive, expired or churned on the status column');
  if (a === 'promo') return plain(r.promo.rows, 'Arrived on a promo code', `${r.promo.codes} different codes between them`);
  if (a === 'nopromo') return plain(r.promo.plainRows, 'No promo code', 'They came in without one');
  /* The chip beside the two cards counts the book, so the list it opens
     has to be the same rows or the heading lies about its own length. */
  if (a === 'never') return plain(r.activity.neverOnBookRows, 'Never signed in', 'The app has never seen these accounts');

  return plain(r.cohort, 'Every client', 'Everybody in this window', 'all', true);
}
